// =============================================================================
// ui/shell/UnitNotice.tsx — why this screen is not in the unit you picked.
// -----------------------------------------------------------------------------
// ── 🚨 A GLOBAL CONTROL THAT SILENTLY DOES NOTHING IS WORSE THAN NO CONTROL ──
//
// The display-unit switch does not reach every screen, and the reader has no way
// to tell "this screen is in dollars" from "this screen ignored me" — and the
// second reading is the one that makes them distrust a figure that is correct.
//
// ── 🚨 BUT THERE ARE TWO REASONS A SCREEN STAYS IN DOLLARS, AND ONE MESSAGE FOR
//    BOTH WAS ITSELF A FALSE CLAIM ──
//
// The first version said "this screen doesn’t do points yet" everywhere. On
// Accounts, Prop Report and the Coach's Report that is simply untrue: those
// figures are dollar facts and will never convert. Telling a reader that a
// settled decision is unfinished work is the same class of defect as a stale
// "not ported yet" card — a confident claim that is wrong — and this file exists
// to prevent exactly that, so it had better not commit it.
//
// So three states, not two:
//
//   CONVERTED   silent. The screen follows the switch.
//   BY DESIGN   says WHY it is in dollars. Not a promise of future work.
//   PENDING     says the conversion has not been done yet.
//
// ⚠ A tab counts as converted only when its SELECTOR and its FORMATTERS moved
// together. Half-converting is the one genuinely dangerous state rather than a
// partial one: the selector returns points and `money()` prints them with a
// dollar sign, which on a $50-a-point contract is a plausible figure out by 50x.
//
// ▶ The PENDING state is whatever is in neither list, so #44's remaining work is
// visible on screen rather than tracked in a comment. A tab joins one of the two
// lists in the same commit that settles it.
//
// ── ⚠ WHY IT LIVES IN ITS OWN FILE ──
//
// It began inside `App.tsx`, where nothing could test it: mounting `App` needs
// every provider and a signed-in session. Josh then asked whether the notice had
// ever appeared and there was no way to answer — the tab he had seen it on was
// converted by then, which HIDES the notice, so his screen could not settle the
// question either way. A component with a claim nobody can check is the shape of
// the defect this whole port keeps hitting. So: one file, one test.
// =============================================================================

import { usePnlUnit } from '../state/usePnlUnit';

/**
 * Tabs whose selector AND formatters both follow the display unit.
 *
 * ⚠ EXPORTED SO THE TEST CAN ASSERT AGAINST IT rather than restating it. A copy
 * of this list in a test file would drift the moment a tab is converted, and
 * would then be asserting the old world in a green suite.
 */
export const UNIT_CONVERTED_TABS: ReadonlySet<string> = new Set([
  'dashboard',
  'dayview',
  'calendar',
  'trades',
  'analytics-overview',
  'analytics-setup',
  'analytics-excursion',
  'journal',
  'patterns',
]);

/**
 * Tabs that stay in dollars PERMANENTLY, and the reason each one does.
 *
 * 🚨 NOT PENDING WORK. Every figure behind these is a dollar fact — a firm's
 * rule, a broker's charge, or a ranking whose whole meaning is money — and
 * converting it would produce a number nobody trades against.
 *
 * ⚠ The reason is stated ON SCREEN rather than left in a comment, because the
 * question the reader is actually asking is "why is this still in dollars?", and
 * "not yet" is a wrong answer to it.
 */
export const UNIT_DOLLAR_BY_DESIGN: Readonly<Record<string, string>> = {
  accounts:
    'balances, drawdown floors and profit targets are your firm’s rules, and firms write them in dollars',
  'analytics-prop':
    'prop rules are set and enforced in dollars — a loss limit in ticks is not a rule anybody has',
  reports:
    'leaks are ranked by what they cost you, and that ranking only means anything in money',
  bestopps:
    'the risk figure is position sizing, which is a dollar decision',
  account:
    'billing is in dollars',
  // 🚨 MOVED HERE FROM THE CONVERTED LIST (#67), and the move IS the fix.
  //
  // `selectors/review` opens with a paragraph headed THE NUMBERS ARE ALWAYS IN
  // DOLLARS, explaining that beta computes this tab with no unit. A later pass
  // wired the switch in anyway — so the tiles converted, the brief printed a
  // points total as "+$249.11" to the trader and to the AI coach, and this list
  // vouched for all of it as correct. The seam no longer accepts a unit.
  review:
    'a weekly review is a money report — beta computes it in dollars and the brief it hands the coach says so',
};

/**
 * The unit as a plain word for a sentence.
 *
 * ⚠ Not `PNL_UNIT_LABELS`, which is menu copy — "Dollars ($)", "R-multiple" —
 * and reads wrong mid-sentence.
 */
const UNIT_WORDS: Record<string, string> = {
  points: 'points', ticks: 'ticks', r: 'R',
};

/**
 * Tabs that show NO MONEY AT ALL, so the switch has nothing to reach.
 *
 * 🔴 A FOURTH STATE, AND IT EXISTS BECAUSE THREE PRODUCED A FALSE CLAIM. The
 * Backtesting tab deals in points and R only — its own subtitle, one line below
 * where this notice printed, reads "Points + R only — no dollars". It was in
 * neither list, so it fell to PENDING and the screen said:
 *
 *   "Showing dollars — this screen doesn't do points yet"
 *
 * Both halves false, on a screen with no dollar figure anywhere, directly above
 * a sentence contradicting it. That is the exact defect this file's header says
 * it exists to prevent, committed by this file.
 *
 * ⚠ IT IS NOT "CONVERTED", AND PUTTING IT THERE WOULD BE A DIFFERENT LIE. That
 * list means the selector and the formatters both FOLLOW the switch. These do
 * not — they are in points and R whatever you pick. Same silence, opposite
 * reason, and a reader of the list would draw the wrong conclusion.
 *
 * ⚠ AND IT IS NOT "BY DESIGN" EITHER: that state prints "In dollars — <reason>",
 * which is false here in its first two words.
 *
 * ▶ Silent is right. There is no mismatch to explain — the reader asked for
 * points and the screen is in points.
 */
export const UNIT_NO_MONEY: ReadonlySet<string> = new Set([
  'backtests',
]);

export function UnitNotice({ tabId }: { tabId: string }) {
  const { unit } = usePnlUnit();

  // Nothing to explain when the reader is already in dollars.
  if (unit === 'usd') return null;
  // Nothing to explain when the screen followed them.
  if (UNIT_CONVERTED_TABS.has(tabId)) return null;
  // Nothing to explain when the screen has no money on it to convert.
  if (UNIT_NO_MONEY.has(tabId)) return null;

  const reason = UNIT_DOLLAR_BY_DESIGN[tabId];
  if (reason) {
    return (
      <p className="unit-notice" data-kind="by-design" role="status">
        In dollars — {reason}.
      </p>
    );
  }

  return (
    <p className="unit-notice" data-kind="pending" role="status">
      Showing dollars — this screen doesn’t do {UNIT_WORDS[unit] ?? unit} yet.
    </p>
  );
}
